import React, { useState } from 'react';
import { useApi } from './hooks/useApi';

const TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$299',
    features: [
      '500 simulations/month',
      '2,500 tickets imported/month',
      '2 active agent configs',
      'Benchmark tab',
      'CSV export',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '$799',
    highlight: true,
    features: [
      '2,500 simulations/month',
      '15,000 tickets imported/month',
      'Unlimited agent configs',
      'A/B config comparison',
      'Everything in Starter',
    ],
  },
];

export default function UpgradeModal({ onClose, onCheckout, currentTier = 'free', isDemo = false, betaCta = '' }) {
  const { authFetch } = useApi();
  const [promoCode, setPromoCode] = useState('');
  const [promoState, setPromoState] = useState(null); // null | 'loading' | 'success' | 'error'
  const [promoMsg, setPromoMsg] = useState('');

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoState('loading');
    setPromoMsg('');
    try {
      const res = await authFetch('/api/billing/apply-promo', {
        method: 'POST',
        body: JSON.stringify({ code: promoCode.trim() }),
      });
      setPromoState('success');
      setPromoMsg(res.message || 'Professional access activated for 90 days.');
      // Close after a short delay so user sees the confirmation
      setTimeout(() => onClose(), 2200);
    } catch (err) {
      setPromoState('error');
      setPromoMsg(err.message || 'Invalid promo code.');
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: 20,
    }}>
      <div style={{
        background: '#1a1a2e', border: '1px solid #2d2d4e', borderRadius: 16,
        padding: 32, width: 560, maxWidth: '95vw',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>{isDemo ? '🚀' : '🚀'}</div>
          <h2 style={{ color: '#e2e8f0', fontWeight: 800, fontSize: 22, margin: 0 }}>
            {isDemo ? 'Join the Beta' : 'Upgrade your plan'}
          </h2>
          <p style={{ color: '#6b7280', fontSize: 14, marginTop: 6 }}>
            {isDemo
              ? 'Apply for beta access and get 90 days of Professional free.'
              : `You've reached your ${currentTier} plan limit.`}
          </p>
        </div>

        {isDemo ? (
          /* ── Demo mode: single beta CTA, no Stripe cards ── */
          <div style={{ marginBottom: 24 }}>
            <div style={{
              background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)',
              borderRadius: 12, padding: '24px 20px', textAlign: 'center', marginBottom: 16,
            }}>
              <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.7, marginBottom: 20 }}>
                Beta testers get <strong style={{ color: '#10b981' }}>90 days of Professional access</strong> free
                — including unlimited configs, A/B comparison, Benchmark tab, and CSV export.
                In exchange we ask for honest feedback and a 30-minute call.
              </div>
              <a
                href={betaCta}
                onClick={onClose}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: '#10b981', color: '#fff', textDecoration: 'none',
                  borderRadius: 8, padding: '12px 28px', fontSize: 14, fontWeight: 700,
                }}
              >
                Apply for Beta Access →
              </a>
            </div>
          </div>
        ) : (
          /* ── Normal mode: Stripe tier cards ── */
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            {TIERS.map(tier => (
              <div key={tier.id} style={{
                background: tier.highlight ? 'rgba(124,58,237,0.1)' : '#0f0f23',
                border: `2px solid ${tier.highlight ? '#7c3aed' : '#2d2d4e'}`,
                borderRadius: 12, padding: 20,
              }}>
                {tier.highlight && (
                  <div style={{
                    fontSize: 10, fontWeight: 700, color: '#7c3aed',
                    letterSpacing: '0.08em', marginBottom: 8,
                  }}>
                    MOST POPULAR
                  </div>
                )}
                <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0', marginBottom: 4 }}>
                  {tier.name}
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#7c3aed', marginBottom: 16 }}>
                  {tier.price}<span style={{ fontSize: 13, color: '#6b7280', fontWeight: 400 }}>/mo</span>
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {tier.features.map(f => (
                    <li key={f} style={{ fontSize: 12, color: '#94a3b8', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                      <span style={{ color: '#10b981', flexShrink: 0 }}>✓</span> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => onCheckout(tier.id)}
                  style={{
                    width: '100%', padding: '9px 0', borderRadius: 8, border: 'none',
                    background: tier.highlight ? '#7c3aed' : '#374151',
                    color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Upgrade to {tier.name}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Promo / beta access code — hidden in demo mode (beta CTA is the path) */}
        {!isDemo && (
        <div style={{
          marginBottom: 12, padding: '14px 16px',
          background: '#0f0f23', border: '1px solid #2d2d4e', borderRadius: 10,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 8 }}>
            Have a beta access or promo code?
          </div>
          {promoState === 'success' ? (
            <div style={{ fontSize: 13, color: '#10b981', fontWeight: 600 }}>
              ✓ {promoMsg}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={promoCode}
                onChange={e => setPromoCode(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && handleApplyPromo()}
                placeholder="Enter code (e.g. BETA2026)"
                disabled={promoState === 'loading'}
                style={{
                  flex: 1, background: '#1a1a2e', border: '1px solid #374151',
                  borderRadius: 6, color: '#e2e8f0', padding: '7px 12px',
                  fontSize: 13, outline: 'none',
                  borderColor: promoState === 'error' ? '#ef4444' : '#374151',
                }}
              />
              <button
                onClick={handleApplyPromo}
                disabled={!promoCode.trim() || promoState === 'loading'}
                style={{
                  padding: '7px 16px', borderRadius: 6, border: 'none',
                  background: promoCode.trim() ? '#7c3aed' : '#374151',
                  color: '#fff', fontSize: 13, fontWeight: 600,
                  cursor: promoCode.trim() ? 'pointer' : 'not-allowed',
                  whiteSpace: 'nowrap',
                }}
              >
                {promoState === 'loading' ? '…' : 'Apply'}
              </button>
            </div>
          )}
          {promoState === 'error' && (
            <div style={{ fontSize: 12, color: '#ef4444', marginTop: 6 }}>
              {promoMsg}
            </div>
          )}
        </div>
        )}

        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '10px 0', borderRadius: 8,
            background: 'transparent', border: '1px solid #374151',
            color: '#6b7280', fontSize: 13, cursor: 'pointer',
          }}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}